import React from 'react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';

/**
 * Initialize the LaunchDarkly provider with the client-side ID and user context
 * @param {Object} user - The user object containing at minimum a key property
 * @returns {Promise<React.ComponentType>} - A LaunchDarkly provider component
 */
export const initLDProvider = async (user) => {
  // Ensure user has required properties
  if (!user || !user.key) {
    console.warn('LaunchDarkly requires a user with at least a key property');
    // Provide a default anonymous user if none is provided
    user = {
      key: 'anonymous-user',
      anonymous: true
    };
  }

  // Get the client-side ID from environment variables
  const clientSideID = process.env.REACT_APP_LAUNCHDARKLY_CLIENT_SIDE_ID;

  // Check if the client-side ID is available
  if (!clientSideID) {
    throw new Error(
      'LaunchDarkly client-side ID is not defined in environment variables. ' +
      'Please set REACT_APP_LAUNCHDARKLY_CLIENT_SIDE_ID in your .env file to initialize LaunchDarkly.'
    );
  }

  // Initialize the LaunchDarkly provider with the client-side ID and user context
  const LDProvider = await asyncWithLDProvider({
    clientSideID,
    user,
    options: {
      streaming: true, // Enable real-time updates (default is true)
      sendEvents: true, // Send events back to LaunchDarkly (default is true)
      allAttributesPrivate: false, // Set to true to make all user attributes private
      privateAttributes: [], // No private attributes - email will be visible in the UI for debugging and targeting
    },
  });

  // Expose the client to window object for debugging
  // This will make it available as soon as LaunchDarkly is initialized
  if (typeof window !== 'undefined' && LDProvider.__ldClientInstance) {
    window.ldClient = LDProvider.__ldClientInstance;
    // Client exposed to window object for debugging
  }

  return LDProvider;
};

/**
 * Create a user object for LaunchDarkly from authentication data
 * @param {Object} authData - Authentication data from your app
 * @returns {Object} - A LaunchDarkly user object
 */
export const createLDUser = (authData) => {
  // No logging needed
  // If no auth data is provided, return an anonymous user
  if (!authData) {
    // Get the current domain even for anonymous users
    const domain = typeof window !== 'undefined' ? window.location.hostname : '';

    return {
      key: 'anonymous-user',
      anonymous: true,
      custom: {
        domain: domain
      }
    };
  }

  // Extract user ID from JWT token or use email as key
  const userId = authData.userId || authData.email || authData.username;

  // Get the current domain
  const domain = typeof window !== 'undefined' ? window.location.hostname : '';

  // Create a LaunchDarkly user object
  const ldUser = {
    key: userId, // Required: A unique identifier for the user
    custom: {
      domain: domain
    }
  };

  // Only add email and name if they're provided
  if (authData.email) {
    ldUser.email = authData.email;
  }

  if (authData.name || authData.firstName || authData.lastName) {
    ldUser.name = authData.name || `${authData.firstName || ''} ${authData.lastName || ''}`.trim();
  }

  return ldUser;
};
